import React, { useState, useEffect } from 'react';

interface SystemSettings {
  price_per_minute: number;
  default_session_duration: number;
  max_session_duration: number;
  free_trial_minutes: number;
  minimum_payment_amount: number;
  payment_providers: {
    alba: boolean;
    stripe: boolean;
    paypal: boolean;
  };
  ai_settings: {
    model: string;
    temperature: number;
    max_tokens: number;
    response_timeout: number;
  };
  feature_flags: {
    webrtc_enabled: boolean;
    telnyx_enabled: boolean;
    recording_enabled: boolean;
    transcription_enabled: boolean;
    analytics_enabled: boolean;
  };
  business_hours: {
    enabled: boolean;
    timezone: string;
    start_time: string;
    end_time: string;
    working_days: string[];
  };
  email_settings: {
    smtp_host: string;
    smtp_port: number;
    from_email: string;
    from_name: string;
  };
}

interface PricingTier {
  id: string;
  min_minutes: number;
  max_minutes: number | null;
  price_per_minute: number;
  tier_name: string;
  description: string;
  is_active: boolean;
}

const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>({
    price_per_minute: 0.50,
    default_session_duration: 30,
    max_session_duration: 120,
    free_trial_minutes: 10,
    minimum_payment_amount: 5,
    payment_providers: {
      alba: true,
      stripe: false,
      paypal: false
    },
    ai_settings: {
      model: 'gpt-4o-realtime-preview',
      temperature: 0.8,
      max_tokens: 4096,
      response_timeout: 30000
    },
    feature_flags: {
      webrtc_enabled: true,
      telnyx_enabled: false,
      recording_enabled: true,
      transcription_enabled: true,
      analytics_enabled: true
    },
    business_hours: {
      enabled: false,
      timezone: 'Europe/Vilnius',
      start_time: '09:00',
      end_time: '18:00',
      working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    },
    email_settings: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      from_email: 'noreply@atsiliepsiu.lt',
      from_name: 'Atsiliepsiu.lt'
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'general' | 'ai' | 'payment' | 'features' | 'email' | 'pricing'>('general');
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    if (activeTab === 'pricing') {
      fetchPricingTiers();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/admin/settings', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Nepavyko gauti nustatymų' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Nustatymai sėkmingai išsaugoti' });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Nepavyko išsaugoti nustatymų' });
    } finally {
      setSaving(false);
    }
  };

  const updateNestedSetting = (category: keyof SystemSettings, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] as any),
        [field]: value
      }
    }));
  };

  const fetchPricingTiers = async () => {
    setPricingLoading(true);
    try {
      const response = await fetch('/api/pricing/tiers', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPricingTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching pricing tiers:', error);
      setMessage({ type: 'error', text: 'Nepavyko gauti kainų' });
    } finally {
      setPricingLoading(false);
    }
  };

  const updatePricingTier = async (tierId: string, updates: Partial<PricingTier>) => {
    try {
      const response = await fetch(`/api/pricing/tiers/${tierId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Kaina atnaujinta sėkmingai' });
        fetchPricingTiers();
      } else {
        throw new Error('Failed to update pricing tier');
      }
    } catch (error) {
      console.error('Error updating pricing tier:', error);
      setMessage({ type: 'error', text: 'Nepavyko atnaujinti kainos' });
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#666'
      }}>
        Kraunami nustatymai...
      </div>
    );
  }

  return (
    <div className="admin-settings" style={{
      background: '#ffffff',
      borderRadius: '8px',
      padding: '2rem',
      color: '#333'
    }}>
      <div className="settings-header" style={{
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <h2 style={{ color: '#000042', margin: 0 }}>⚙️ Sistemos nustatymai</h2>
        <p style={{ color: '#666', marginTop: '0.5rem' }}>
          Valdykite visus sistemos nustatymus vienoje vietoje
        </p>
      </div>

      {message && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '6px',
          background: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message.text}
        </div>
      )}

      <div className="settings-tabs" style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <button
          onClick={() => setActiveTab('general')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'general' ? '#000042' : 'transparent',
            color: activeTab === 'general' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          Pagrindiniai
        </button>
        <button
          onClick={() => setActiveTab('ai')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'ai' ? '#000042' : 'transparent',
            color: activeTab === 'ai' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          AI Nustatymai
        </button>
        <button
          onClick={() => setActiveTab('payment')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'payment' ? '#000042' : 'transparent',
            color: activeTab === 'payment' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          Mokėjimai
        </button>
        <button
          onClick={() => setActiveTab('features')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'features' ? '#000042' : 'transparent',
            color: activeTab === 'features' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          Funkcijos
        </button>
        <button
          onClick={() => setActiveTab('email')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'email' ? '#000042' : 'transparent',
            color: activeTab === 'email' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          El. paštas
        </button>
        <button
          onClick={() => setActiveTab('pricing')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeTab === 'pricing' ? '#000042' : 'transparent',
            color: activeTab === 'pricing' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer'
          }}
        >
          💰 Kainos
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'general' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Kaina už minutę (€)
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.price_per_minute}
                onChange={(e) => setSettings({...settings, price_per_minute: parseFloat(e.target.value)})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Numatyta sesijos trukmė (min)
              </label>
              <input
                type="number"
                value={settings.default_session_duration}
                onChange={(e) => setSettings({...settings, default_session_duration: parseInt(e.target.value)})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Maksimali sesijos trukmė (min)
              </label>
              <input
                type="number"
                value={settings.max_session_duration}
                onChange={(e) => setSettings({...settings, max_session_duration: parseInt(e.target.value)})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Nemokamos bandomosios minutės
              </label>
              <input
                type="number"
                value={settings.free_trial_minutes}
                onChange={(e) => setSettings({...settings, free_trial_minutes: parseInt(e.target.value)})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Minimalus mokėjimo dydis (€)
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.minimum_payment_amount}
                onChange={(e) => setSettings({...settings, minimum_payment_amount: parseFloat(e.target.value)})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'ai' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                AI Modelis
              </label>
              <select
                value={settings.ai_settings.model}
                onChange={(e) => updateNestedSetting('ai_settings', 'model', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="gpt-4o-realtime-preview">GPT-4o Realtime Preview</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </select>
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Temperature (0-1)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={settings.ai_settings.temperature}
                onChange={(e) => updateNestedSetting('ai_settings', 'temperature', parseFloat(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Maksimalus token skaičius
              </label>
              <input
                type="number"
                value={settings.ai_settings.max_tokens}
                onChange={(e) => updateNestedSetting('ai_settings', 'max_tokens', parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Atsakymo laukimo laikas (ms)
              </label>
              <input
                type="number"
                value={settings.ai_settings.response_timeout}
                onChange={(e) => updateNestedSetting('ai_settings', 'response_timeout', parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'payment' && (
          <div>
            <h3 style={{ marginBottom: '1rem' }}>Mokėjimo nustatymai</h3>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Paysera mokėjimų sistema jau integruota.
            </p>
          </div>
        )}

        {activeTab === 'features' && (
          <div>
            <h3 style={{ marginBottom: '1rem' }}>Funkcijų įjungimas/išjungimas</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={settings.feature_flags.webrtc_enabled}
                  onChange={(e) => updateNestedSetting('feature_flags', 'webrtc_enabled', e.target.checked)}
                />
                WebRTC Balso pokalbiai
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={settings.feature_flags.telnyx_enabled}
                  onChange={(e) => updateNestedSetting('feature_flags', 'telnyx_enabled', e.target.checked)}
                />
                Telnyx telefonų integracija
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={settings.feature_flags.recording_enabled}
                  onChange={(e) => updateNestedSetting('feature_flags', 'recording_enabled', e.target.checked)}
                />
                Pokalbių įrašymas
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={settings.feature_flags.transcription_enabled}
                  onChange={(e) => updateNestedSetting('feature_flags', 'transcription_enabled', e.target.checked)}
                />
                Pokalbių transkripcija
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={settings.feature_flags.analytics_enabled}
                  onChange={(e) => updateNestedSetting('feature_flags', 'analytics_enabled', e.target.checked)}
                />
                Analitikos rinkimas
              </label>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                SMTP serveris
              </label>
              <input
                type="text"
                value={settings.email_settings.smtp_host}
                onChange={(e) => updateNestedSetting('email_settings', 'smtp_host', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                SMTP portas
              </label>
              <input
                type="number"
                value={settings.email_settings.smtp_port}
                onChange={(e) => updateNestedSetting('email_settings', 'smtp_port', parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Siuntėjo el. paštas
              </label>
              <input
                type="email"
                value={settings.email_settings.from_email}
                onChange={(e) => updateNestedSetting('email_settings', 'from_email', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>

            <div className="setting-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Siuntėjo vardas
              </label>
              <input
                type="text"
                value={settings.email_settings.from_name}
                onChange={(e) => updateNestedSetting('email_settings', 'from_name', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#000042' }}>
              💰 Kainų pakopų valdymas
            </h3>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Čia galite redaguoti kainų pakopas. Visos kainų pakeitimas automatiškai atsinaujins visose vietose (registracija, atsipirkimas, /kainos puslapis).
            </p>

            {pricingLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                Kraunamos kainos...
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {pricingTiers.map((tier) => (
                  <div
                    key={tier.id}
                    style={{
                      background: '#f8f9fa',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: tier.is_active ? '2px solid #28a745' : '2px solid #ddd'
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr 100px', gap: '1rem', alignItems: 'center' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                          Pakopa
                        </label>
                        <input
                          type="text"
                          value={tier.tier_name}
                          onChange={(e) => {
                            setPricingTiers(prev => prev.map(t =>
                              t.id === tier.id ? { ...t, tier_name: e.target.value } : t
                            ));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                          Nuo (min)
                        </label>
                        <input
                          type="number"
                          value={tier.min_minutes}
                          onChange={(e) => {
                            setPricingTiers(prev => prev.map(t =>
                              t.id === tier.id ? { ...t, min_minutes: parseInt(e.target.value) } : t
                            ));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                          Iki (min)
                        </label>
                        <input
                          type="number"
                          value={tier.max_minutes || ''}
                          placeholder="∞"
                          onChange={(e) => {
                            setPricingTiers(prev => prev.map(t =>
                              t.id === tier.id ? { ...t, max_minutes: e.target.value ? parseInt(e.target.value) : null } : t
                            ));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                          Kaina už minutę (€)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={tier.price_per_minute}
                          onChange={(e) => {
                            setPricingTiers(prev => prev.map(t =>
                              t.id === tier.id ? { ...t, price_per_minute: parseFloat(e.target.value) } : t
                            ));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }}
                        />
                      </div>

                      <button
                        onClick={() => updatePricingTier(tier.id, {
                          tier_name: tier.tier_name,
                          min_minutes: tier.min_minutes,
                          max_minutes: tier.max_minutes,
                          price_per_minute: tier.price_per_minute
                        })}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Išsaugoti
                      </button>
                    </div>

                    <div style={{ marginTop: '0.5rem' }}>
                      <input
                        type="text"
                        value={tier.description}
                        placeholder="Aprašymas..."
                        onChange={(e) => {
                          setPricingTiers(prev => prev.map(t =>
                            t.id === tier.id ? { ...t, description: e.target.value } : t
                          ));
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '0.9rem',
                          fontStyle: 'italic'
                        }}
                      />
                    </div>

                    <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                      <strong>Pavyzdys:</strong> {tier.min_minutes} min = €{Math.round(tier.min_minutes * tier.price_per_minute)} |
                      {tier.max_minutes && ` ${tier.max_minutes} min = €${Math.round(tier.max_minutes * tier.price_per_minute)}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              background: '#fff3cd',
              borderRadius: '8px',
              border: '1px solid #ffc107'
            }}>
              <strong style={{ color: '#856404' }}>⚠️ Pastaba:</strong>
              <p style={{ margin: '0.5rem 0 0 0', color: '#856404' }}>
                Kainų pakeitimai bus matomi visose šiose vietose:
              </p>
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', color: '#856404' }}>
                <li>/kainos puslapis (OperatorPricingSection)</li>
                <li>Registracijos forma (minutų pasirinkimas)</li>
                <li>Vartotojo dashboard (papildyti balansą)</li>
                <li>API skaičiavimai (backend)</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div style={{
        marginTop: '2rem',
        paddingTop: '2rem',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem'
      }}>
        <button
          onClick={() => fetchSettings()}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Atšaukti
        </button>
        <button
          onClick={saveSettings}
          disabled={saving}
          style={{
            padding: '0.75rem 1.5rem',
            background: saving ? '#6c757d' : '#000042',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saugoma...' : 'Išsaugoti nustatymus'}
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;